import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { 
  PhoneIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  StarIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

export default function CallLogs() {
  const { getUserBusinessPhones, isSuperAdmin, userBusinesses } = useAuth()
  const [calls, setCalls] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [timeframe, setTimeframe] = useState('week')
  const [selectedCall, setSelectedCall] = useState(null)
  const [showCallModal, setShowCallModal] = useState(false)

  useEffect(() => {
    loadCallsAndCustomers()
  }, [statusFilter, typeFilter, timeframe])

  const loadCallsAndCustomers = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('supabase_token') // or however you store your auth token
      
      if (!token) {
        console.error('No auth token found')
        setCalls([])
        setCustomers([])
        setLoading(false)
        return
      }
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://sayhi-voice-engine-production.up.railway.app'
    
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
  
      console.log('🌐 Frontend: Loading calls and customers from backend:', BACKEND_URL)
  
      // Load calls and customers from your Railway backend API
      const [callsResponse, customersResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/call-logs?timeframe=${timeframe}&statusFilter=${statusFilter}&typeFilter=${typeFilter}`, {
          headers
        }),
        fetch(`${BACKEND_URL}/api/dashboard/customers`, {
          headers
        })
      ])  
  
      if (!callsResponse.ok || !customersResponse.ok) {
        throw new Error('Failed to fetch data from backend')
      }
  
      const callsData = await callsResponse.json()
      const customersData = await customersResponse.json()
  
      if (callsData.success && customersData.success) {
        // Create a map of customers by phone number for quick lookup
        const customerMap = new Map()
        customersData.customers.forEach(customer => {
          customerMap.set(customer.phone_number, customer)
        })
  
        // Merge calls with customer data
        const callsWithCustomers = callsData.calls.map(call => ({
          ...call,
          customer: customerMap.get(call.customer_phone) || null
        }))
  
        setCalls(callsWithCustomers)
        setCustomers(customersData.customers)
      } else {
        setCalls([])
        setCustomers([])
      }
  
    } catch (error) {
      console.error('Error loading calls and customers:', error)
      setCalls([])
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }
  
  const loadCalls = async (businessFilter) => {
    try {
      let query = supabase.from('call_logs').select('*')

      // Apply business filtering
      if (businessFilter.length > 0) {
        query = query.in('business_phone', businessFilter)
      }

      // Apply time filter
      const timeFilter = getTimeFilter(timeframe)
      if (timeFilter) {
        query = query.gte('call_start_time', timeFilter)
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('call_status', statusFilter)
      }

      // Apply type filter
      if (typeFilter === 'urgent') {
        query = query.or('emergency_escalation.eq.true,call_type.eq.emergency,call_type.eq.escalation')
      } else if (typeFilter === 'normal') {
        query = query.eq('emergency_escalation', false).neq('call_type', 'emergency').neq('call_type', 'escalation')
      }

      query = query.order('call_start_time', { ascending: false }).limit(100)

      const { data, error } = await query

      if (error) {
        console.error('Error loading calls:', error)
        return { success: false, data: [] }
      }

      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error in loadCalls:', error)
      return { success: false, data: [] }
    }
  }

  const loadCustomers = async (businessFilter) => {
    try {
      let query = supabase.from('customers').select('*')

      // Apply business filtering by getting business IDs
      if (businessFilter.length > 0) {
        const { data: businesses } = await supabase
          .from('business_info')
          .select('id')
          .in('phone_number', businessFilter)
        
        if (businesses && businesses.length > 0) {
          const businessIds = businesses.map(b => b.id)
          query = query.in('business_id', businessIds)
        } else {
          return { success: true, data: [] }
        }
      }

      const { data, error } = await query

      if (error) {
        console.error('Error loading customers:', error)
        return { success: false, data: [] }
      }

      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error in loadCustomers:', error)
      return { success: false, data: [] }
    }
  }

  const getTimeFilter = (timeframe) => {
    const now = new Date()
    let startDate = new Date()
    
    switch (timeframe) {
      case 'today':
        startDate.setHours(0, 0, 0, 0)
        break
      case 'week':
        startDate.setDate(now.getDate() - 7)
        break
      case 'month':
        startDate.setMonth(now.getMonth() - 1)
        break
      case 'all':
        return null
      default:
        startDate.setDate(now.getDate() - 7)
    }

    return startDate.toISOString()
  }

  const filteredCalls = calls.filter(call => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      call.customer_phone?.toLowerCase().includes(searchLower) ||
      call.conversation_summary?.toLowerCase().includes(searchLower) ||
      call.services_mentioned?.some(service => service.toLowerCase().includes(searchLower)) ||
      call.customer?.name?.toLowerCase().includes(searchLower) ||
      call.customer?.email?.toLowerCase().includes(searchLower) ||
      call.customer?.address?.toLowerCase().includes(searchLower)
    )
  })

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Unknown Number'
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1)
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`
    }
    return phone
  }

  const getCallTypeLabel = (call) => {
    if (call.emergency_escalation || call.call_type === 'emergency') {
      return 'Urgent Call'
    }
    if (call.call_type === 'escalation') {
      return 'Escalated'
    }
    return 'Regular Call'
  }

  const getCallStatusLabel = (status) => {
    switch(status) {
      case 'completed': return 'Completed'
      case 'in_progress': return 'Active'
      case 'failed': return 'Failed'
      case 'missed': return 'Missed'
      default: return 'Completed'
    }
  }

  const getCallDuration = (call) => {
    const seconds = call.call_duration_seconds || call.duration_seconds || 0
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const formatCallTime = (dateString) => {
    if (!dateString) return 'Unknown time'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    
    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`
    } else if (diffHours < 24) {
      return `${diffHours}h ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const getPriorityLevel = (call) => {
    if (call.emergency_escalation) return 'urgent'
    if (call.call_type === 'escalation') return 'high'
    return 'normal'
  }

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200'
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200'
      default: return 'text-green-600 bg-green-50 border-green-200'
    }
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'failed': return 'bg-red-100 text-red-800'
      case 'missed': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCustomerTypeLabel = (customer) => {
    if (!customer) return null
    if (customer.vip_status) return 'VIP'
    if (customer.total_calls > 5) return 'Frequent'
    if (customer.total_calls > 1) return 'Returning'
    return 'New'
  }

  const getCustomerTypeColor = (customer) => {
    if (!customer) return ''
    if (customer.vip_status) return 'bg-purple-100 text-purple-800'
    if (customer.total_calls > 5) return 'bg-blue-100 text-blue-800'
    if (customer.total_calls > 1) return 'bg-green-100 text-green-800'
    return 'bg-gray-100 text-gray-800'
  }

  const openCallDetails = (call) => {
    setSelectedCall(call)
    setShowCallModal(true)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your call history...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b">
        <div className="px-6 py-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <PhoneIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Call History</h1>
                <p className="text-sm text-gray-600">Review all your customer conversations</p>
              </div>
            </div>
            <button
              onClick={loadCallsAndCustomers}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm transition-all duration-200"
            >
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by phone, name, email, or conversation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                />
              </div>
            </div>

            {/* Timeframe */}
            <div>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              >
                <option value="all">All Calls</option>
                <option value="urgent">Urgent Only</option>
                <option value="normal">Regular Only</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="missed">Missed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Total Calls</p>
                <p className="text-2xl font-bold text-gray-900">{filteredCalls.length}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <PhoneIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">With Customer Info</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredCalls.filter(call => call.customer?.name).length}
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">VIP Customers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredCalls.filter(call => call.customer?.vip_status).length}
                </p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <StarIconSolid className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Avg Duration</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredCalls.length > 0 
                    ? Math.round(filteredCalls.reduce((acc, call) => acc + (call.call_duration_seconds || call.duration_seconds || 0), 0) / filteredCalls.length)
                    : 0}s
                </p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Calls List */}
        <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
          {filteredCalls.length === 0 ? (
            <div className="text-center py-16">
              <PhoneIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No calls found</h3>
              <p className="text-gray-500 mb-4">
                {calls.length === 0 
                  ? "No calls have been made yet. When customers call, they'll appear here."
                  : "Try adjusting your filters to see more calls."
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredCalls.map((call, index) => (
                <div 
                  key={call.call_id || index} 
                  className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => openCallDetails(call)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center relative">
                        <PhoneIcon className="h-6 w-6 text-white" />
                        {call.customer?.vip_status && (
                          <div className="absolute -top-1 -right-1">
                            <StarIconSolid className="h-5 w-5 text-yellow-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <div className="flex items-center space-x-2">
                            {call.customer?.name ? (
                              <h3 className="text-sm font-medium text-gray-900">
                                {call.customer.name}
                              </h3>
                            ) : (
                              <h3 className="text-sm font-medium text-gray-600">
                                Unknown Customer
                              </h3>
                            )}
                            <span className="text-sm text-gray-500">
                              {formatPhoneNumber(call.customer_phone)}
                            </span>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(getPriorityLevel(call))}`}>
                            {getCallTypeLabel(call)}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(call.call_status)}`}>
                            {getCallStatusLabel(call.call_status)}
                          </span>
                          {call.customer && getCustomerTypeLabel(call.customer) && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCustomerTypeColor(call.customer)}`}>
                              {getCustomerTypeLabel(call.customer)}
                            </span>
                          )}
                        </div>
                        
                        {/* Customer Contact Info */}
                        {call.customer && (call.customer.email || call.customer.address) && (
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mb-1">
                            {call.customer.email && (
                              <div className="flex items-center">
                                <EnvelopeIcon className="h-3 w-3 mr-1" />
                                {call.customer.email}
                              </div>
                            )}
                            {call.customer.address && (
                              <div className="flex items-center">
                                <MapPinIcon className="h-3 w-3 mr-1" />
                                {call.customer.address.length > 40 
                                  ? call.customer.address.substring(0, 40) + '...'
                                  : call.customer.address}
                              </div>
                            )}
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-600">
                          {call.conversation_summary ? 
                            call.conversation_summary.substring(0, 120) + (call.conversation_summary.length > 120 ? '...' : '') : 
                            'Tap to view call details'
                          }
                        </p>
                        {call.services_mentioned && call.services_mentioned.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {call.services_mentioned.slice(0, 3).map((service, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {service}
                              </span>
                            ))}
                            {call.services_mentioned.length > 3 && (
                              <span className="text-xs text-gray-500">+{call.services_mentioned.length - 3} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{formatCallTime(call.call_start_time)}</p>
                      <p className="text-xs text-gray-500 mt-1">{getCallDuration(call)}</p>
                      {call.customer?.total_calls > 1 && (
                        <p className="text-xs text-blue-600 mt-1">
                          {call.customer.total_calls} total calls
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Call Details Modal */}
      {showCallModal && selectedCall && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Call Details</h3>
              <button
                onClick={() => setShowCallModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Customer Information */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2" />
                    Customer Information
                  </h4>
                  {selectedCall.customer ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Name</label>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedCall.customer.name}
                          {selectedCall.customer.vip_status && (
                            <StarIconSolid className="h-4 w-4 text-purple-500 inline ml-1" />
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Phone</label>
                        <p className="text-sm text-gray-900">{formatPhoneNumber(selectedCall.customer_phone)}</p>
                      </div>
                      {selectedCall.customer.email && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Email</label>
                          <p className="text-sm text-gray-900">{selectedCall.customer.email}</p>
                        </div>
                      )}
                      {selectedCall.customer.address && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Address</label>
                          <p className="text-sm text-gray-900">{selectedCall.customer.address}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-gray-600">Customer Status</label>
                        <p className="text-sm text-gray-900">
                          {getCustomerTypeLabel(selectedCall.customer)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Total Calls</label>
                        <p className="text-sm text-gray-900">{selectedCall.customer.total_calls}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <UserIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">No customer information available</p>
                      <p className="text-xs text-gray-500 mt-1">{formatPhoneNumber(selectedCall.customer_phone)}</p>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Call Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium">{getCallTypeLabel(selectedCall)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{getCallDuration(selectedCall)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium">{getCallStatusLabel(selectedCall.call_status)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">When:</span>
                      <span className="font-medium">
                        {selectedCall.call_start_time ? new Date(selectedCall.call_start_time).toLocaleString() : 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedCall.services_mentioned && selectedCall.services_mentioned.length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Services Discussed</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCall.services_mentioned.map((service, i) => (
                        <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Conversation Details */}
              <div className="md:col-span-2 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                    Call Summary
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedCall.conversation_summary || 'No summary available for this call.'}
                  </p>
                </div>

                {selectedCall.customer_sentiment && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Customer Satisfaction</h4>
                    <div className="flex items-center space-x-2">
                      <div className={`h-3 w-3 rounded-full ${
                        selectedCall.customer_sentiment === 'positive' ? 'bg-green-400' :
                        selectedCall.customer_sentiment === 'negative' ? 'bg-red-400' : 'bg-yellow-400'
                      }`}></div>
                      <span className="capitalize font-medium">
                        {selectedCall.customer_sentiment || 'Neutral'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Full Transcript */}
                {selectedCall.transcript && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Full Conversation
                    </h4>
                    <div className="max-h-60 overflow-y-auto bg-white rounded border p-3">
                      <div className="text-sm text-gray-700 space-y-2">
                        {selectedCall.transcript.split('\n').map((line, i) => (
                          <div key={i} className="leading-relaxed">
                            {line.includes('Agent:') ? (
                              <div className="text-blue-800 font-medium">{line}</div>
                            ) : line.includes('Customer:') ? (
                              <div className="text-green-800 font-medium">{line}</div>
                            ) : (
                              <div>{line}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCallModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}